// Unique, hand-written neighborhood landing page content.
// Each entry has substantially different copy to avoid duplicate-content penalties.

export const NEIGHBORHOODS = {
  "petworth": {
    slug: "dispensary-petworth-dc",
    name: "Petworth",
    fullName: "Petworth, Washington, DC",
    h1Lead: "Your neighborhood",
    h1Highlight: "Petworth dispensary",
    intro: "DWC is the dispensary in the heart of Petworth. Our flagship sits at 4915 Georgia Ave NW — between Sherman Circle and Kennedy Street, a short walk from the Petworth Metro on the Green & Yellow lines. If you live in Petworth, we are your local dispensary.",
    primaryKeyword: "dispensary in Petworth",
    metaDescription: "DWC is the medical cannabis dispensary in Petworth, Washington DC — at 4915 Georgia Ave NW, near Sherman Circle and the Petworth Metro. Premium flower, edibles, vapes & same-day delivery to Petworth.",
    bodySections: [
      {
        h2: "A real dispensary in Petworth — not 20 minutes away",
        copy: [
          "Most DC dispensary lists send Petworth residents downtown. We are the alternative. DWC is physically in Petworth, on the same Georgia Ave you walk down every day. No Metro ride. No parking near 14th & U. Just a five-minute walk from Sherman Circle, Upshur Street, or the Petworth Recreation Center.",
          "We also deliver across every Petworth ZIP — from 11th Street and Quincy down to Grant Circle and Park Place, plus the blocks bordering 16th Street Heights and Crestwood. Most Petworth delivery windows are under 45 minutes."
        ],
      },
      {
        h2: "What Petworth patients usually pick up",
        copy: [
          "Petworth is a neighborhood of long days — Walter Reed commuters, Howard students, families with kids in DCPS, hospitality workers from Park View and Columbia Heights. Our most-asked-for products reflect that: balanced hybrids for end-of-day, indica-leaning flower and 5 mg gummies for sleep, low-dose vapes for anxiety, and tinctures for patients managing chronic pain without smoking.",
          "Our patient specialists are trained to match by need first, THC% second. If you tell us \"I want to sleep but not feel groggy,\" you will get a real recommendation — not the SKU with the best margin."
        ],
      },
      {
        h2: "Petworth landmarks within walking distance of DWC",
        copy: [
          "DWC Uptown sits on the busiest commercial stretch of Petworth. We are minutes from the Petworth Library, Slim's Diner, Hitching Post, Timber Pizza, Cinder BBQ, Looking Glass, and Yes! Organic. Stop in on your way home from groceries, or after dinner at Domku."
        ],
      },
      {
        h2: "Same-day Petworth weed delivery",
        copy: [
          "We deliver across Petworth, Park View, Brightwood, 16th Street Heights, Manor Park, and Crestwood. Place your order through our online menu (launching soon) or call us. Delivery is reserved for valid DC medical cardholders or registered patients with reciprocity from another state."
        ],
      },
    ],
    ctaTitle: "Visit DWC in Petworth.",
    ctaCopy: "4915 Georgia Ave NW · Open Sun–Mon & Wed–Sat 12–8pm",
  },

  "georgia-ave-nw": {
    slug: "dispensary-georgia-ave-nw",
    name: "Georgia Ave NW",
    fullName: "Georgia Ave NW, Washington, DC",
    h1Lead: "The",
    h1Highlight: "Georgia Ave dispensary",
    intro: "If you searched \"dispensary Georgia Ave,\" you already found us. DWC sits at 4915 Georgia Ave NW — the upper stretch of one of DC's most-traveled corridors, right between Petworth and Brightwood. We serve every neighborhood that touches Georgia Ave from Howard University north to the Maryland line.",
    primaryKeyword: "Georgia Ave dispensary",
    metaDescription: "Looking for a dispensary on Georgia Ave NW? District Wellness Center is at 4915 Georgia Ave NW, Washington DC. Premium cannabis, walk-in friendly, same-day delivery up and down the Georgia Ave corridor.",
    bodySections: [
      {
        h2: "Why Georgia Ave is the right place for a dispensary",
        copy: [
          "Georgia Ave runs the spine of upper Northwest — Howard University to the Silver Spring border. It carries Petworth, Park View, Pleasant Plains, Brightwood, Manor Park, Shepherd Park and Takoma DC. More people live within a 10-minute walk of Georgia Ave than almost any other DC corridor.",
          "DWC chose 4915 Georgia Ave NW specifically because it's the easiest dispensary to reach for the patients who actually live in upper Northwest. Bus 70 and 79 both stop near us. Petworth Metro is a 5-minute walk. Parking is available, and Capital Bikeshare has a dock on the same block."
        ],
      },
      {
        h2: "Areas we serve from our Georgia Ave dispensary",
        copy: [
          "From our 4915 Georgia Ave NW storefront we deliver same-day to: Petworth, Park View, Pleasant Plains, Brightwood, 16th Street Heights, Crestwood, Manor Park, Brightwood Park, Shepherd Park, Takoma DC, Fort Totten, Lamond-Riggs, Riggs Park, Brookland (Catholic U side), Columbia Heights, Mt. Pleasant, and the upper edge of Adams Morgan."
        ],
      },
      {
        h2: "What sets DWC apart on Georgia Ave",
        copy: [
          "Every product on our shelves has been reviewed against DC OCA compliance and lab-tested. We work directly with licensed DC cultivators and processors — not gifting shops, not unregulated brands. If you've ever bought something on Georgia Ave that didn't feel right, that's exactly what we exist to replace.",
          "Licensed by the DC Alcoholic Beverage and Cannabis Administration. License ABCA-127954. Owned and operated by Growth and Wellness, LLC, trading as District Wellness Center."
        ],
      },
    ],
    ctaTitle: "Walk into the original Georgia Ave dispensary.",
    ctaCopy: "4915 Georgia Ave NW · Petworth · Sun–Mon & Wed–Sat 12–8pm",
  },

  "columbia-heights": {
    slug: "dispensary-columbia-heights-dc",
    name: "Columbia Heights",
    fullName: "Columbia Heights, Washington, DC",
    h1Lead: "Cannabis delivery to",
    h1Highlight: "Columbia Heights",
    intro: "Columbia Heights patients get our fastest delivery windows in the District. From our 4915 Georgia Ave NW dispensary, we typically reach 14th & Park Road, 11th & Monroe, and the blocks around the Columbia Heights Metro in under 30 minutes.",
    primaryKeyword: "Columbia Heights cannabis delivery",
    metaDescription: "Same-day medical cannabis delivery to Columbia Heights, Washington DC. Licensed dispensary serving 14th Street, Park Road, Mt. Pleasant border and the DCUSA corridor. Order online from DWC.",
    bodySections: [
      {
        h2: "Columbia Heights neighborhoods we serve",
        copy: [
          "We deliver to every block of Columbia Heights — from the row houses on Holmead and Princeton south to Florida Ave; east to Park Morton and Park View; west to Mt. Pleasant and the Adams Morgan border. If you live near the Tivoli, Wonderland, Meridian Pint, El Chucho, Bad Saint, or Pho Viet — you're squarely in our daily delivery zone."
        ],
      },
      {
        h2: "Walk-in or delivery — your choice",
        copy: [
          "Columbia Heights residents have two simple options: take the 70/79 bus or a 10-minute drive up Georgia Ave to walk into our 4915 dispensary, or order delivery and we'll bring it to your door. Most weeknight delivery windows are under an hour. Weekends and DCUSA-traffic days can be a little longer — we'll be honest with you about the ETA before you commit."
        ],
      },
      {
        h2: "Built for the Columbia Heights schedule",
        copy: [
          "Columbia Heights residents tend to want two things from a dispensary: fast service and trustworthy product. The neighborhood is full of people who care what's in what they consume. Every item on our menu has been lab-tested and sourced from DC-licensed cultivators. Ask any patient specialist for the COA — we'll show you on the spot."
        ],
      },
    ],
    ctaTitle: "Delivering to Columbia Heights today.",
    ctaCopy: "Open Sun–Mon & Wed–Sat 12–8pm · 4915 Georgia Ave NW",
  },

  "shaw": {
    slug: "dispensary-shaw-dc",
    name: "Shaw",
    fullName: "Shaw, Washington, DC",
    h1Lead: "Same-day delivery to",
    h1Highlight: "Shaw",
    intro: "Shaw is one of our most-requested delivery zones. From 4915 Georgia Ave NW we reach 7th Street, T Street, U Street's quieter east end, LeDroit Park, and Bloomingdale in roughly 25–45 minutes — typically faster than the apps charge for it.",
    primaryKeyword: "Shaw weed delivery",
    metaDescription: "Licensed medical cannabis delivery to Shaw, Washington DC. Serving LeDroit Park, Bloomingdale, the U Street corridor and the Howard University area from DWC's 4915 Georgia Ave NW dispensary.",
    bodySections: [
      {
        h2: "Shaw delivery from a real DC dispensary",
        copy: [
          "We deliver across all of Shaw — the historic core around 7th & T, the Convention Center side, the Howard University side via Georgia Ave, plus LeDroit Park, Bloomingdale, Truxton Circle, and the U Street corridor's east end. If you're near the Shaw library, Right Proper, Calabash Tea, Crane & Turtle, Big Bear Café, or Boundary Stone — we deliver to you the same day."
        ],
      },
      {
        h2: "Why patients in Shaw switch to DWC",
        copy: [
          "Shaw has more cannabis options than most DC neighborhoods. Patients tell us they switch to DWC for three reasons: licensed compliance (we're regulated by the DC ABCA, license ABCA-127954), product transparency (every batch is lab-tested with COA on request), and unhurried service (we'd rather take 5 extra minutes finding you the right thing than push the bag with the highest markup)."
        ],
      },
      {
        h2: "Pickup option — 10 minutes up Georgia Ave",
        copy: [
          "Don't want to wait for delivery? Most of Shaw is a 10–15 minute drive (or one bus on the 70) up Georgia Ave to our 4915 storefront. Walk-ins welcome 7 days a week — Mon–Thu 8am–10pm, Fri–Sat 8am–11pm, Sun 9am–9pm."
        ],
      },
    ],
    ctaTitle: "Delivering to Shaw today.",
    ctaCopy: "Place your order — most Shaw windows are under an hour",
  },

  "adams-morgan": {
    slug: "dispensary-adams-morgan-dc",
    name: "Adams Morgan",
    fullName: "Adams Morgan, Washington, DC",
    h1Lead: "Cannabis delivered to",
    h1Highlight: "Adams Morgan",
    intro: "From 18th Street nightlife to the row houses along Kalorama and Lanier Heights — DWC delivers premium medical cannabis to every block of Adams Morgan from our flagship dispensary on Georgia Ave NW.",
    primaryKeyword: "Adams Morgan cannabis delivery",
    metaDescription: "Same-day medical cannabis delivery to Adams Morgan, Washington DC — 18th Street, Columbia Road, Kalorama. Licensed DC dispensary serving Adams Morgan from 4915 Georgia Ave NW.",
    bodySections: [
      {
        h2: "Adams Morgan delivery without the markup",
        copy: [
          "Adams Morgan is one of DC's most expensive neighborhoods to live in — and one of the most over-priced when it comes to cannabis. Patients pay tourist-trap rates within a 10-block radius of 18th & Columbia. DWC fixes that. We're a 10-minute drive away on Georgia Ave, our menu is competitive on price, and delivery to Adams Morgan is included on most order sizes."
        ],
      },
      {
        h2: "Where in Adams Morgan we deliver",
        copy: [
          "Every block. The full 18th Street strip, Columbia Road, the Lanier Heights row houses, the Kalorama side near Mintwood and Bourbon Steak, Champlain Street, Reed Street, and the blocks bordering Mt. Pleasant. If you can walk to The Diner or Bukom Café, you're in our zone."
        ],
      },
      {
        h2: "What Adams Morgan patients usually order",
        copy: [
          "Adams Morgan is a neighborhood that goes out — restaurants, bars, late nights. The most-ordered products from this zip code are clean live resin carts, low-dose edibles for casual nights, and indica-leaning flower for the wind-down. Our staff is happy to help you build a small starter kit if you're new to cannabis."
        ],
      },
    ],
    ctaTitle: "Adams Morgan delivery, same day.",
    ctaCopy: "Open 7 days · Mon–Thu 8am–10pm · Fri–Sat 8am–11pm · Sun 9am–9pm",
  },

  "capitol-hill": {
    slug: "dispensary-capitol-hill-dc",
    name: "Capitol Hill",
    fullName: "Capitol Hill, Washington, DC",
    h1Lead: "Cannabis delivery to",
    h1Highlight: "Capitol Hill",
    intro: "Capitol Hill is one of our top weeknight delivery zones. From DWC at 4915 Georgia Ave NW, we deliver to every block from H Street NE down to Barracks Row and out to Lincoln Park. Most Capitol Hill windows are under an hour.",
    primaryKeyword: "Capitol Hill weed delivery",
    metaDescription: "Same-day medical cannabis delivery to Capitol Hill, Washington DC. Licensed DC dispensary serving Eastern Market, H Street NE, Lincoln Park, Barracks Row and the entire Hill from DWC.",
    bodySections: [
      {
        h2: "Capitol Hill delivery zones we cover",
        copy: [
          "We deliver to every Capitol Hill ZIP — Eastern Market, the row houses around Lincoln Park, H Street NE up through Atlas Performing Arts, the Stanton Park blocks, the Senate side near Union Station, and the Navy Yard side down toward Barracks Row. If you can walk to Eastern Market on a Saturday, you're squarely in our service area."
        ],
      },
      {
        h2: "Hill staffers, federal workers, residents — different needs, same standard",
        copy: [
          "Capitol Hill is a unique mix: Hill staffers on tight schedules, federal employees mindful of policies, longtime residents, young families. Our patient specialists treat every order with the same discretion. Our packaging is plain, our deliveries are unmarked, and your purchase is logged only to comply with DC ABCA tracking requirements — never shared."
        ],
      },
      {
        h2: "Pickup is also an option",
        copy: [
          "If delivery doesn't fit your schedule, our 4915 Georgia Ave NW dispensary is a 15-minute drive up North Capitol or a Lyft from Union Station. Walk-ins welcome 7 days a week — Mon–Thu 8am–10pm, Fri–Sat 8am–11pm, Sun 9am–9pm."
        ],
      },
    ],
    ctaTitle: "Capitol Hill delivery — order now.",
    ctaCopy: "Licensed DC dispensary · License ABCA-127954",
  },
};

export const NEIGHBORHOOD_LIST = Object.values(NEIGHBORHOODS);
